import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Route, Switch } from 'react-router';
import { withRouter } from 'react-router-dom';
import { alertActions } from './actions';
import { history } from './helpers';
import { PrivateRoute } from './components/PrivateRoute';
import { AdminRoute } from './components/AdminRoute';
import Home from './components/Home/Home';
import Login from './components/Home/Login';
import HOCRViewer from './components/DocumentViewer/HOCRViewer';
import Overview from './components/Home/Overview';
import UploadView from './components/Upload/UploadView';
import DocumentsView from './components/Home/DocumentList/DocumentsView';
import Layout from './components/Home/Layout';
import MissingClauseList from './components/MissingConcepts/MissingConceptList';
import DashboardView from './components/Dashboard/DashboardView';
import AuthService from './services/auth.service';
import ExportView from './components/DocumentViewer/ExportView';
import ExternalSSOLogin from "./components/ExternalSSOLogin";
import TrialPage from "./components/TrialPage";
import ThemeSelectUserView from './components/CategoryView/UserView/ThemeSelectUserView';
import ThemeUserView from './components/CategoryView/UserView/ThemeUserView';
import NotFoundPage from './components/NotFoundPage';
import ErrorBoundary from './components/ErrorBoundary';
import PortfolioAnalysisList from './components/AnalysisReports/PortfolioList/PortfolioAnalysisList';
import PortfolioAnalysisView from './components/AnalysisReports/PortfolioAnalysisView';
import NewPortfolioAnalysisView from './components/AnalysisReports/NewPortfolioAnalysis/NewPortfolioAnalysisView';
import FreemiumSessionService from './services/freemium-session.service';
import FreemiumSessionHelp from './components/FreemiumSessionHelp';

class App extends Component {
    constructor(props) {
        super(props);

        this.authService = new AuthService();
        this.freemiumSessionService = new FreemiumSessionService();
        this.state = { sessionReady: false, sessionUnavailable: false };
        history.listen((location, action) => {
            // clear alert on location change            
            this.props.clear();
        });
    }

    componentDidMount() {
        this.freemiumSessionService.bootstrap().then((session) => {
            this.authService.setUserContextForLogging();
            this.setState({
                sessionReady: true,
                sessionUnavailable: session.unavailable === true
            });
        });
    }

    renderHome() {
        let resultComponent = <PrivateRoute path='/' component={(props) => (<Layout><Home /></Layout>)} />;

        if (!this.authService.isAuthenticated() && !this.authService.isGuest()) {
            resultComponent = this.redirectToLogin();
        }

        return resultComponent;
    }

    redirectToLogin = () => {
        if (this.authService.isAuthenticated()) return <PrivateRoute path='/' component={(props) => (<Layout><Home /></Layout>)} />;
        else if (this.authService.isGuest()) return <Login showOptions={true} />
        else {
            this.authService.login();
            let text = "Redirecting to the authentication service...";
            return <Login text={text} />
        }
    }

    startSession(history) {
        this.authService.handleAuthentication(history);
        let text = "Logging you in...";
        return <Login text={text} />
    }

    render() {
        if (!this.state.sessionReady) return <Login text="Loading your session..." />;
        if (this.state.sessionUnavailable && this.authService.isFreemium()) return <FreemiumSessionHelp />;

        let idPathRegex = '[0-9a-fA-F]{24}';

        return (
            <ErrorBoundary>
                {this.props.message &&
                    <div className={`alert ${this.props.type}`}>{this.props.message}</div>
                }
                <Switch>
                    <Route path="/limited-review" component={(props) => (
                        <TrialPage {...props} />
                    )}
                    />
                    <Route path="/try-legly" component={(props) => (
                        <TrialPage {...props} />
                    )}
                    />
                    <Route path="/login/sso" component={(props) => (
                        <ExternalSSOLogin {...props} />
                    )}
                    />
                    <Route path="/login" render={() => this.redirectToLogin()} />
                    <Route path="/startSession" render={({ history }) => this.startSession(history)} />
                    <Route exact path="/freemium-session-help" component={FreemiumSessionHelp} />
                    <Route exact path="/" render={() => this.renderHome()} />

                    <PrivateRoute path='/upload' component={(props) => (
                        <Layout {...props}>
                            <UploadView auth={this.authService} />
                        </Layout>
                    )}
                    />

                    <AdminRoute path='/dashboard' component={(props) => (
                        <Layout {...props}>
                            <DashboardView />
                        </Layout>
                    )}
                    />

                    <PrivateRoute path='/themes/:type' component={(props) => (
                        <Layout {...props}>
                            <ThemeUserView />
                        </Layout>
                    )}
                    />

                    <PrivateRoute path='/themes' component={(props) => (
                        <Layout {...props}>
                            <ThemeSelectUserView />
                        </Layout>
                    )}
                    />

                    <PrivateRoute path='/documents' component={(props) => (
                        <Layout {...props}>
                            <DocumentsView />
                        </Layout>
                    )}
                    />

                    <PrivateRoute path={`/:docId(${idPathRegex})/missing`} component={(props) => (
                        <Layout {...props}>
                            <MissingClauseList />
                        </Layout>
                    )}
                    />
                    <PrivateRoute path={`/:docId(${idPathRegex})/viewer/:clause`} component={(props) => (
                        <Layout {...props}>
                            <HOCRViewer />
                        </Layout>
                    )}
                    />
                    <PrivateRoute path={`/:docId(${idPathRegex})/export`} component={(props) => (
                        <Layout {...props}>
                            <ExportView />
                        </Layout>
                    )}
                    />
                    <PrivateRoute path={`/:docId(${idPathRegex})`} component={(props) => (
                        <Layout {...props}>
                            <Overview />
                        </Layout>
                    )}
                    />

                    <PrivateRoute path={`/reports/:reportId(${idPathRegex})/document/:docId(${idPathRegex})/missing`} component={(props) => (
                        <Layout {...props}>
                            <MissingClauseList />
                        </Layout>
                    )}
                    />
                    <PrivateRoute path={`/reports/:reportId(${idPathRegex})/document/:docId(${idPathRegex})/viewer/:clause`} component={(props) => (
                        <Layout {...props}>
                            <HOCRViewer />
                        </Layout>
                    )}
                    />
                    <PrivateRoute path={`/reports/:reportId(${idPathRegex})/document/:docId(${idPathRegex})`} component={(props) => (
                        <Layout {...props}>
                            <Overview />
                        </Layout>
                    )}
                    />
                    <Route path={`/reports/:reportId(${idPathRegex})`} children={(props) => (
                        <Layout {...props}>
                            <PortfolioAnalysisView />
                        </Layout>
                    )}
                    />

                    <AdminRoute path='/reports/create' component={(props) => (
                        <Layout {...props}>
                            <NewPortfolioAnalysisView />
                        </Layout>
                    )}
                    />

                    <PrivateRoute path='/reports' component={(props) => (
                        <Layout {...props}>
                            <PortfolioAnalysisList />
                        </Layout>
                    )}
                    />

                    <Route path='*' component={(props) => (
                        <Layout {...props}>
                            <NotFoundPage />
                        </Layout>
                    )}
                    />
                </Switch>
            </ErrorBoundary>
        );
    }
}

export default withRouter(connect(
    state => state.alert,
    dispatch => bindActionCreators(alertActions, dispatch)
)(App));



// WEBPACK FOOTER //
// ./src/App.js